import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../utils/offerLayoutConfigs';
import { getPreset } from '../../../textStyle.js';
import { getListIconHTML } from '../../../utils/offerListIcons';

// Simplified zone height fallback for static HTML export (no runtime state needed)
const getZoneHeightStyle = (zoneId, defaultHeight) => ({ minHeight: defaultHeight || '60px' });

export const generateElementHTML = (element) => {
    const styles = element.styles || {};

    // Create enhanced styles that match the preview rendering
    const enhancedStyles = { ...styles };

    // Apply mobile-optimized styling to match preview
    switch (element.type) {
      case ELEMENT_TYPES.HEADING:
        // Preserve original heading styles
        enhancedStyles.marginBottom = enhancedStyles.margin || '16px';
        // Don't override fontSize - preserve original
        enhancedStyles.fontFamily = enhancedStyles.fontFamily || 'inherit';
        enhancedStyles.fontWeight = enhancedStyles.fontWeight || 'inherit';
        enhancedStyles.textAlign = enhancedStyles.textAlign || 'inherit';
        enhancedStyles.color = enhancedStyles.color || 'inherit';

        if (styles.backgroundColor && styles.backgroundColor !== 'transparent') {
          enhancedStyles.padding = enhancedStyles.padding || '8px 12px';
          enhancedStyles.borderRadius = enhancedStyles.borderRadius || '4px';
          enhancedStyles.display = 'inline-block';
        } else {
          enhancedStyles.padding = enhancedStyles.padding || '0';
          enhancedStyles.display = 'block';
        }
        break;

      case ELEMENT_TYPES.TEXT:
        // Preserve original text styles
        enhancedStyles.marginBottom = '0'; // Remove bottom margin for inline layout
        enhancedStyles.whiteSpace = 'pre-wrap';
        // Don't override fontSize - preserve original
        enhancedStyles.fontFamily = enhancedStyles.fontFamily || 'inherit';
        enhancedStyles.fontWeight = enhancedStyles.fontWeight || 'inherit';
        enhancedStyles.textAlign = enhancedStyles.textAlign || 'inherit';
        enhancedStyles.color = enhancedStyles.color || 'inherit';

        if (styles.backgroundColor && styles.backgroundColor !== 'transparent') {
          enhancedStyles.padding = enhancedStyles.padding || '8px 12px';
          enhancedStyles.borderRadius = enhancedStyles.borderRadius || '4px';
          enhancedStyles.display = 'block';
        } else {
          enhancedStyles.padding = enhancedStyles.padding || '0';
          enhancedStyles.display = 'block'; // Use block for full width
        }
        enhancedStyles.width = '100%'; // Take full width of the zone
        enhancedStyles.margin = enhancedStyles.margin || '0';
        break;
    }

    const styleString = Object.entries(enhancedStyles)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');

    switch (element.type) {
      case ELEMENT_TYPES.HEADING:
        return `<h1 style="${styleString}">${element.content}</h1>`;
      case ELEMENT_TYPES.TEXT:
        return `<p style="${styleString}">${element.content || ''}</p>`;
      case ELEMENT_TYPES.STAR_RATING: {
        const starsHtml = Array.from({ length: element.maxStars || 5 })
          .map((_, i) => `<span style="font-size: ${element.styles?.fontSize || '24px'}; color: ${i < (element.rating || 5) ? element.styles?.color || '#ffc107' : '#e0e0e0'}; display: inline-flex; align-items: center;">★</span>`)
          .join('');
        const hasText = element.showRatingText;
        const textStr = element.ratingText !== undefined ? element.ratingText : `${element.rating || 5}/${element.maxStars || 5}`;
        const pos = element.ratingTextPosition || 'left';
        const posSpacing = ''; // gap handled by flex gap on inner container
        const ratingPresetCss = element.ratingTextStyles
          ? Object.entries(element.ratingTextStyles).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`).join('; ')
          : '';
        const txtStyle = `font-weight: bold; font-size: inherit; color: inherit; line-height: 1; ${ratingPresetCss}`;
        const textHtml = hasText ? `<span style="${txtStyle}">${textStr}</span>` : '';
        const isColumn = pos === 'top' || pos === 'bottom';
        const flexDir = isColumn ? 'column' : 'row';
        const alignItems = isColumn ? 'flex-start' : 'center';
        const textGap = element.ratingTextGap !== undefined ? element.ratingTextGap : '8px';
        const starsBox = `<div style="display: flex; align-items: center; gap: ${element.styles?.gap || '4px'}">${starsHtml}</div>`;
        const contentHtml = (!hasText || pos === 'left' || pos === 'top') ? `${textHtml}${starsBox}` : `${starsBox}${textHtml}`;
        const innerGroup = `<div style="display: inline-flex; align-items: ${alignItems}; flex-direction: ${flexDir}; gap: ${textGap}; color: ${element.styles?.color || '#ffc107'}; font-size: ${element.styles?.fontSize || '24px'};">${contentHtml}</div>`;
        return `<div style="width: 100%; display: flex; justify-content: ${element.styles?.justifyContent || 'center'}; text-align: ${element.styles?.textAlign || 'center'}; margin-bottom: ${element.styles?.marginBottom || '10px'};">${innerGroup}</div>`;
      }
      case ELEMENT_TYPES.STAR_RATING_TABLE: {
        const titleHtml = element.showTitle !== false && element.title ? `<h6 style="color: ${element.styles?.titleColor || '#000000'}; font-size: ${element.styles?.titleFontSize || '20px'}; font-weight: bold; margin-top: 0; margin-bottom: 20px; text-align: left; line-height: 1.2;">${element.title}</h6>` : '';
        const rowsHtml = (element.rows || []).map((row, idx) => {
          const starsHtml = Array.from({ length: element.maxStars || 5 })
            .map((_, i) => {
              const rated = i < row.rating;
              const color = rated ? (element.styles?.starColor || element.styles?.color || '#ffc107') : '#e0e0e0';
              return `<span style="font-size: ${element.styles?.fontSize || '22px'}; color: ${color}; line-height: 1;">★</span>`;
            })
            .join('');

          const dividerStyle = element.showRowDividers && idx < (element.rows || []).length - 1 ? `border-bottom: ${element.dividerThickness || '1px'} solid ${element.dividerColor || '#eeeeee'};` : '';
          const paddingStyle = `padding-top: ${idx === 0 ? '0' : '15px'}; padding-bottom: ${idx === (element.rows || []).length - 1 ? '0' : '15px'};`;

          const isSideBySide = element.rowLayout === 'side-by-side';
          const labelAlign = element.labelAlign || 'left';

          const textPos = element.ratingTextPosition || 'right';
          const tAlign = element.ratingTextAlign || 'left';
          const sAlign = element.starsAlign || (isSideBySide ? 'right' : 'left');

          const textStyle = `color: ${element.styles?.color || '#1a1a1a'}; font-size: ${element.styles?.labelFontSize || '18px'}; font-family: ${element.styles?.fontFamily || 'inherit'}; font-weight: ${element.styles?.fontWeight || 'bold'};`;

          const starsContent = `
            <div style="display: flex; align-items: center; gap: ${element.styles?.gap || '4px'}; flex: ${sAlign === 'center' ? 'none' : '1'}; justify-content: ${sAlign === 'center' ? 'center' : sAlign === 'right' ? 'flex-end' : 'flex-start'}; order: ${textPos === 'left' ? '2' : '1'};">
              ${starsHtml}
            </div>
          `;

          const textContent = row.ratingText ? `
            <div style="flex: ${tAlign === 'center' ? 'none' : '1'}; text-align: ${tAlign}; order: ${textPos === 'left' ? '1' : '2'}; ${textStyle}">
              ${row.ratingText}
            </div>
          ` : '';

          const innerContentHtml = `
            <div style="display: flex; align-items: center; gap: 15px; flex: 2; width: 100%;">
              ${textContent}${starsContent}
            </div>
          `;

          if (isSideBySide) {
            return `
              <div style="${paddingStyle} ${dividerStyle} display: flex; align-items: center; justify-content: space-between; gap: 15px;">
                <div style="line-height: 1.4; text-align: ${labelAlign}; flex: 1; min-width: 100px; ${textStyle}">${row.label}</div>
                ${innerContentHtml}
              </div>
            `;
          }

          return `
            <div style="${paddingStyle} ${dividerStyle}">
              <div style="margin-bottom: 5px; line-height: 1.4; text-align: ${labelAlign}; ${textStyle}">${row.label}</div>
              ${innerContentHtml}
            </div>
          `;
        }).join('');

        const containerStyle = `
          background-color: ${element.styles?.backgroundColor || '#ffffff'};
          padding: ${element.styles?.padding || '24px'};
          border-radius: ${element.styles?.borderRadius || '12px'};
          border: ${element.styles?.border || '1px solid #eeeeee'};
          margin-bottom: ${element.styles?.marginBottom || '20px'};
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          width: 100%;
          box-sizing: border-box;
          font-family: inherit;
        `;

        const containerAlign = element.styles?.justifyContent || 'flex-start';
        const innerRowsHtml = `<div style="display: flex; flex-direction: column; align-items: ${containerAlign};">${rowsHtml}</div>`;
        return `<div style="${containerStyle}">${titleHtml}${innerRowsHtml}</div>`;
      }
      case ELEMENT_TYPES.IMAGE:
        return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><img src="${element.content}" alt="${element.alt || ''}" style="${styleString}" /></div>`;
      case ELEMENT_TYPES.CROPPABLE_IMAGE:
        return element.croppedImage
          ? `<img src="${element.croppedImage}" alt="Cropped" style="width:${element.styles?.width || 300}px;height:${element.styles?.height || 200}px;object-fit:cover;display:block;margin:auto;border-radius:${element.styles?.borderRadius || '8px'};" />`
          : '';
      case ELEMENT_TYPES.CIRCULAR_IMAGE:
        return element.croppedImage
          ? `<img src="${element.croppedImage}" alt="Cropped" style="width:${element.styles?.width || 200}px;height:${element.styles?.height || 200}px;border-radius:50%;object-fit:cover;display:block;margin:auto;" />`
          : '';
      case ELEMENT_TYPES.FITTABLE_IMAGE: {
        // Use imageSrc or fallback to content property
        const imageSource = element.imageSrc || element.content;
        if (!imageSource) return '';

        // Handle responsive frame dimensions for HTML export
        const getFrameWidthHTML = (styleWidth) => {
          if (styleWidth === "100%" || styleWidth === "auto") {
            return "100%";
          }
          if (typeof styleWidth === 'number') return `${styleWidth}px`;
          if (typeof styleWidth === 'string') {
            const numValue = parseInt(styleWidth, 10);
            return isNaN(numValue) ? "400px" : `${numValue}px`;
          }
          return "400px";
        };

        const getFrameHeightHTML = (styleHeight) => {
          if (styleHeight === "auto") return "300px";
          if (typeof styleHeight === 'number') return `${styleHeight}px`;
          if (typeof styleHeight === 'string') {
            const numValue = parseInt(styleHeight, 10);
            return isNaN(numValue) ? "300px" : `${numValue}px`;
          }
          return "300px";
        };

        const frameWidth = getFrameWidthHTML(element.styles?.width);
        const frameHeight = getFrameHeightHTML(element.styles?.height);
        const position = element.imagePosition || { x: 0, y: 0 };
        const scale = element.imageScale || 1;
        return `<div style="width:${frameWidth};height:${frameHeight};border:none;border-radius:8px;overflow:hidden;position:relative;margin:auto;">
            <img src="${imageSource}" alt="Fittable Image" style="position:absolute;left:50%;top:0;transform:translate(-50%, ${position.y}px) scale(${scale});transform-origin:top center;max-width:none;max-height:none;" />
          </div>`;
      }
      case ELEMENT_TYPES.BUTTON:
        // Handle custom titlecase transformation for HTML export
        let buttonContent = element.content;
        let buttonStyles = enhancedStyles;

        if (element.styles?.textTransform === 'titlecase') {
          // Apply title case transformation to content
          buttonContent = element.content.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
          // Remove the custom textTransform from styles for HTML
          delete buttonStyles.textTransform;
          // Rebuild styleString without the custom textTransform
          const buttonStyleString = Object.entries(buttonStyles)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');
          // Add icons to button content for titlecase HTML generation
          let htmlButtonContent = buttonContent;
          if (element.iconLeft === 'cart') {
            htmlButtonContent = `<span style="color: #d4a000; font-size: 2.2rem; margin-right: 18px;">🛒</span>${htmlButtonContent}`;
          }
          if (element.iconRight === 'arrow') {
            htmlButtonContent = `${htmlButtonContent}<span style="color: #222; font-size: 2.2rem; margin-left: 18px;">➡️</span>`;
          }

          return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><a href="${element.url}" style="${buttonStyleString}">${htmlButtonContent}</a></div>`;
        }

        // Add icons to button content for HTML generation
        let htmlButtonContent = buttonContent;
        if (element.iconLeft === 'cart') {
          htmlButtonContent = `<span style="color: #d4a000; font-size: 2.2rem; margin-right: 18px;">🛒</span>${htmlButtonContent}`;
        }
        if (element.iconRight === 'arrow') {
          htmlButtonContent = `${htmlButtonContent}<span style="color: #222; font-size: 2.2rem; margin-left: 18px;">➡️</span>`;
        }

        return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><a href="${element.url}" style="${styleString}">${htmlButtonContent}</a></div>`;
      case ELEMENT_TYPES.VIDEO:
        // Handle video file uploads
        if (element.videoFile) {
          // Uploaded video file
          const videoWidth = element.styles?.width || '100%';
          const videoHeight = element.styles?.height || 315;

          if (videoWidth === '100%' || videoWidth === 'auto') {
            // Check if aspectRatio is explicitly set in styles (e.g., "1/1" for square)
            let paddingBottomPercent;
            if (element.styles?.aspectRatio && element.styles.aspectRatio !== 'auto') {
              // Parse aspect ratio string (e.g., "1/1", "16/9", "9/16")
              const [widthRatio, heightRatio] = element.styles.aspectRatio.split('/').map(Number);
              if (widthRatio && heightRatio) {
                // Calculate padding-bottom percentage: (height / width) * 100%
                paddingBottomPercent = (heightRatio / widthRatio) * 100;
              } else {
                // Invalid aspect ratio, fall back to height calculation
                const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
                paddingBottomPercent = (heightNum / 400) * 100;
              }
            } else {
              // No aspectRatio set, use padding-bottom technique based on height
              const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
              paddingBottomPercent = (heightNum / 400) * 100;
            }

            return `<div style="text-align: center; margin-bottom: 4px; width: calc(100% + 32px); max-width: calc(100% + 32px); padding: 0; margin-left: -16px; margin-right: -16px;">
              <div style="position: relative; width: 100%; max-width: 100%; padding-bottom: ${paddingBottomPercent}%; height: 0; overflow: hidden; margin: 0; padding-left: 0; padding-right: 0;">
                <video 
                  src="${element.videoFile}" 
                  style="position: absolute; top: 0; left: 0; width: 100%; max-width: 100%; height: 100%; margin: 0; padding: 0; object-fit: contain;" 
                  controls 
                  playsinline
                  webkit-playsinline="true"
                  preload="metadata"
                  ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''}
                ></video>
              </div>
            </div>`;
          } else {
            // Ensure full width even when custom width is set
            const fullWidthStyle = `${styleString}; width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important;`;
            return `<div style="text-align: center; margin-bottom: 4px; width: 100%; max-width: 100%; padding: 0; margin-left: 0; margin-right: 0;">
              <video 
                src="${element.videoFile}" 
                style="${fullWidthStyle}" 
                controls 
                playsinline
                webkit-playsinline="true"
                preload="metadata"
                ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''}
              ></video>
            </div>`;
          }
        } else {
          // No video uploaded
          return `<div style="text-align: center; margin-bottom: 4px; padding: 40px; background-color: #f5f5f5; border: 2px dashed #ccc; border-radius: 0;">
            <p style="color: #666; margin: 0;">No video uploaded</p>
          </div>`;
        }

      case ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY: {
        const videoWidth = element.styles?.width || '100%';
        const videoHeight = element.styles?.height || 315;

        const aspectRatio = element.styles?.aspectRatio || '16/9';
        const [w, h] = aspectRatio.split('/').map(Number);
        const isVertical = w && h && h > w;

        let dynamicBottom = '24px';
        if (isVertical) {
          const ratioValue = h / w;
          if (ratioValue > 2) {
            dynamicBottom = '100px';
          } else if (ratioValue > 1.5) {
            dynamicBottom = '60px';
          } else if (ratioValue > 1) {
            dynamicBottom = '48px';
          }
        }

        const align = element.styles?.textAlign || 'center';
        const buttonLeft = align === 'center' ? '50%' : (align === 'right' ? 'auto' : '32px');
        const buttonRight = align === 'right' ? '32px' : 'auto';
        const buttonTransform = align === 'center' ? 'translateX(-50%)' : 'none';

        let paddingBottomPercent = 100;
        if (element.styles?.aspectRatio && element.styles.aspectRatio !== 'auto') {
          const [widthRatio, heightRatio] = element.styles.aspectRatio.split('/').map(Number);
          if (widthRatio && heightRatio) {
            paddingBottomPercent = (heightRatio / widthRatio) * 100;
          }
        } else {
          const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
          paddingBottomPercent = (heightNum / 400) * 100;
        }

        const videoContent = element.videoFile ? `
          <video 
            src="${element.videoFile}" 
            style="position: absolute; top: 0; left: 0; width: 100%; max-width: 100%; height: 100%; margin: 0; padding: 0; object-fit: contain;" 
            controls 
            playsinline
            webkit-playsinline="true"
            preload="metadata"
            ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''}
          ></video>
        ` : `
          <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #000; color: #fff;">
            <p>No video uploaded</p>
          </div>
        `;

        // Generate styles for the overlay button
        const buttonStyles = {
          ...(element.styles || {}),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '44px',
          minHeight: '44px',
          textAlign: 'center',
          textDecoration: 'none',
          boxSizing: 'border-box'
        };

        const buttonStyleString = Object.entries(buttonStyles)
          .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
          .join('; ');

        return `
          <div style="text-align: center; margin-bottom: 4px; width: calc(100% + 32px); max-width: calc(100% + 32px); padding: 0; margin-left: -16px; margin-right: -16px; position: relative;">
            <div style="position: relative; width: 100%; max-width: 100%; padding-bottom: ${paddingBottomPercent}%; height: 0; overflow: hidden; margin: 0; padding-left: 0; padding-right: 0; border-radius: 0; background-color: #000;">
              ${videoContent}
              
              <div style="position: absolute; bottom: ${dynamicBottom}; left: ${buttonLeft}; right: ${buttonRight}; transform: ${buttonTransform}; width: 55%; max-width: 66.6%; z-index: 20; display: flex; justify-content: ${align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start')};">
                <a href="${element.url || '#'}" style="${buttonStyleString}">${element.content || 'Click Here'}</a>
              </div>
            </div>
          </div>
        `;
      }
      case ELEMENT_TYPES.HTML:
        return element.content;
      case ELEMENT_TYPES.DIVIDER:
        return `<hr style="${styleString}" />`;

      case ELEMENT_TYPES.COMPARISON_TABLE:
        const titlePreset = getPreset(element.titleStylePreset || 'compareTableTableTitle') || {};
        const productNamePreset = getPreset(element.productNameStylePreset || 'compareTableProdctName') || {};
        const featureTitlePreset = getPreset(element.featureTitleStylePreset || 'compareTableFeatureTitle') || {};
        const featureDescPreset = getPreset(element.featureDescStylePreset || 'compareTableFeatureDescription') || {};

        const comparisonTableHTML = `
          <div style="margin-bottom: 4px;">
            ${element.title ? `<h3 style="text-align: ${titlePreset.textAlign || 'center'}; margin-bottom: 20px; font-family: ${titlePreset.fontFamily || 'inherit'}; font-size: ${titlePreset.fontSize || 'inherit'}; font-weight: ${titlePreset.fontWeight || 'bold'}; color: ${titlePreset.color || 'inherit'}; line-height: ${titlePreset.lineHeight || 'inherit'}; letter-spacing: ${titlePreset.letterSpacing || 'inherit'};">${element.title}</h3>` : ''}
            <table style="width: 100%; border-collapse: collapse; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden;">
              <thead>
                ${(element.productAImage || element.productBImage) ? `
                  <tr>
                    <td style="width: 40%; border: none; padding: 8px;"></td>
                    <td style="width: 30%; border: none; padding: 8px; text-align: center; background-color: #B8E6E1;">
                      ${element.productAImage ? `<img src="${element.productAImage}" alt="${element.productA}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />` : ''}
                    </td>
                    <td style="width: 30%; border: none; padding: 8px; text-align: center; background-color: #ffebee;">
                      ${element.productBImage ? `<img src="${element.productBImage}" alt="${element.productB}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />` : ''}
                    </td>
                  </tr>
                ` : ''}
                <tr>
                  <th style="padding: 16px; font-weight: bold; font-size: 16px; width: 40%; background-color: #f5f5f5; text-align: left;">Feature</th>
                  <th style="padding: 16px; width: 30%; background-color: #B8E6E1; text-align: ${productNamePreset.textAlign || 'center'}; font-family: ${productNamePreset.fontFamily || 'inherit'}; font-size: ${productNamePreset.fontSize || '16px'}; font-weight: ${productNamePreset.fontWeight || 'bold'}; color: ${productNamePreset.color || '#2D5A5A'}; line-height: ${productNamePreset.lineHeight || 'inherit'}; letter-spacing: ${productNamePreset.letterSpacing || 'inherit'};">${element.productA}</th>
                  <th style="padding: 16px; width: 30%; background-color: #ffebee; text-align: ${productNamePreset.textAlign || 'center'}; font-family: ${productNamePreset.fontFamily || 'inherit'}; font-size: ${productNamePreset.fontSize || '16px'}; font-weight: ${productNamePreset.fontWeight || 'bold'}; color: ${productNamePreset.color || '#c62828'}; line-height: ${productNamePreset.lineHeight || 'inherit'}; letter-spacing: ${productNamePreset.letterSpacing || 'inherit'};">${element.productB}</th>
                </tr>
              </thead>
              <tbody>
                ${element.rows.map((row, index) => `
                  <tr style="background-color: ${index % 2 === 0 ? '#fff' : '#f9f9f9'};">
                    <td style="padding: 16px; vertical-align: top;">
                      <div style="margin-bottom: 4px; font-family: ${featureTitlePreset.fontFamily || 'inherit'}; font-size: ${featureTitlePreset.fontSize || 'inherit'}; font-weight: ${featureTitlePreset.fontWeight || 'medium'}; color: ${featureTitlePreset.color || 'inherit'}; text-align: ${featureTitlePreset.textAlign || 'left'}; line-height: ${featureTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${featureTitlePreset.letterSpacing || 'inherit'};">${row.feature}</div>
                      ${row.description ? `<div style="font-family: ${featureDescPreset.fontFamily || 'inherit'}; font-size: ${featureDescPreset.fontSize || '14px'}; font-weight: ${featureDescPreset.fontWeight || 'inherit'}; color: ${featureDescPreset.color || '#666'}; text-align: ${featureDescPreset.textAlign || 'left'}; line-height: ${featureDescPreset.lineHeight || 'inherit'}; letter-spacing: ${featureDescPreset.letterSpacing || 'inherit'};">${row.description}</div>` : ''}
                    </td>
                    <td style="padding: 16px; text-align: center; background-color: ${row.productA ? '#B8E6E1' : '#fff'};">
                      ${row.productA ? '✓' : '✗'}
                    </td>
                    <td style="padding: 16px; text-align: center; background-color: ${row.productB ? '#B8E6E1' : '#ffebee'};">
                      ${row.productB ? '✓' : '✗'}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
        return comparisonTableHTML;

      case ELEMENT_TYPES.TWO_COLUMN_COMPARISON:
        const columnTitlePreset = getPreset(element.titleStylePreset || 'compareTableTwoColumnColumnTitle') || {};
        const itemPreset = getPreset(element.itemStylePreset || 'compareTableTwoColumnItemTitle') || {};

        const twoColumnComparisonHTML = `
          <div style="margin-bottom: 4px;">
            <div style="display: flex; min-height: 400px; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <!-- Left Column - Negative Points -->
              <div style="flex: 1; background-color: #fff; border: 1px solid #e0e0e0; border-right: 0.5px solid #e0e0e0; padding: 24px; display: flex; flex-direction: column;">
                ${element.leftColumnImage ? `
                  <div style="display: flex; justify-content: center; margin-bottom: 16px;">
                    <img src="${element.leftColumnImage}" alt="${element.leftColumnTitle}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;" />
                  </div>
                ` : ''}
                <h3 style="text-align: ${columnTitlePreset.textAlign || 'center'}; margin-bottom: 24px; font-family: ${columnTitlePreset.fontFamily || 'inherit'}; font-size: ${columnTitlePreset.fontSize || '16px'}; font-weight: ${columnTitlePreset.fontWeight || 'bold'}; color: ${columnTitlePreset.color || '#000'}; line-height: ${columnTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${columnTitlePreset.letterSpacing || 'inherit'};">${element.leftColumnTitle}</h3>
                <div style="flex-grow: 1;">
                  ${element.leftItems.map(item => `
                    <div style="display: flex; align-items: flex-start; margin-bottom: 16px; gap: 16px;">
                      <svg style="width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0;" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#dc3545"/>
                        <path d="M15.59 7L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12z" fill="white"/>
                      </svg>
                      <div style="font-family: ${itemPreset.fontFamily || 'inherit'}; font-size: ${itemPreset.fontSize || '12px'}; font-weight: ${itemPreset.fontWeight || '500'}; color: ${itemPreset.color || '#000'}; text-align: ${itemPreset.textAlign || 'left'}; line-height: ${itemPreset.lineHeight || '1.4'}; letter-spacing: ${itemPreset.letterSpacing || 'inherit'};">${item}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
              <!-- Right Column - Positive Points -->
              <div style="flex: 1; background-color: #f0f9f0; border: 1px solid #e0e0e0; border-left: 0.5px solid #e0e0e0; padding: 24px; display: flex; flex-direction: column;">
                ${element.rightColumnImage ? `
                  <div style="display: flex; justify-content: center; margin-bottom: 16px;">
                    <img src="${element.rightColumnImage}" alt="${element.rightColumnTitle}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;" />
                  </div>
                ` : ''}
                <h3 style="text-align: ${columnTitlePreset.textAlign || 'center'}; margin-bottom: 24px; font-family: ${columnTitlePreset.fontFamily || 'inherit'}; font-size: ${columnTitlePreset.fontSize || '16px'}; font-weight: ${columnTitlePreset.fontWeight || 'bold'}; color: ${columnTitlePreset.color || '#000'}; line-height: ${columnTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${columnTitlePreset.letterSpacing || 'inherit'};">${element.rightColumnTitle}</h3>
                <div style="flex-grow: 1;">
                  ${element.rightItems.map(item => `
                    <div style="display: flex; align-items: flex-start; margin-bottom: 16px; gap: 16px;">
                      <svg style="color: #28a745; width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0;" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <div style="font-family: ${itemPreset.fontFamily || 'inherit'}; font-size: ${itemPreset.fontSize || '12px'}; font-weight: ${itemPreset.fontWeight || '500'}; color: ${itemPreset.color || '#000'}; text-align: ${itemPreset.textAlign || 'left'}; line-height: ${itemPreset.lineHeight || '1.4'}; letter-spacing: ${itemPreset.letterSpacing || 'inherit'};">${item}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
          </div>
        `;
        return twoColumnComparisonHTML;

      case ELEMENT_TYPES.CHECKLIST:
        const checklistHTML = `
          <div style="${styleString}">
            ${element.title ? `<h2 style="text-align: center; color: #1976d2; margin-bottom: 24px;">${element.title}</h2>` : ''}
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px;">
              ${element.items.map(item => `
                <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background-color: #f5f5f5; border-radius: 4px; margin-bottom: 8px;">
                  <span style="color: #4caf50;">✓</span>
                  <span style="font-weight: 500;">${item.text.replace(/\n/g, '<br>')}</span>
                </div>
              `).join('')}
            </div>
          </div>
        `;
        return checklistHTML;
      case ELEMENT_TYPES.BUTTON_WITH_NOTICE:
        const getVariantStylesHTML = (variant, styleVariant = 'default') => {
          const styleVariants = {
            'urgent-sale': {
              'default': {
                backgroundColor: '#ff69b4', // pink
                noticeColor: '#101022', // black text on pink
                buttonBg: '#101022', // black button
                buttonColor: '#ffffff', // white text on black
                border: 'none',
                borderRadius: '8px'
              },
              'modern': {
                backgroundColor: '#ffffff', // white
                noticeColor: '#101022', // black text on white (changed from orange/yellow)
                buttonBg: '#ff69b4', // pink button
                buttonColor: '#101022', // black text on pink
                border: '2px solid #ff69b4',
                borderRadius: '12px'
              },
              'minimal': {
                backgroundColor: '#fafafa', // light gray (close to white)
                noticeColor: '#101022', // black text (changed from orange/yellow)
                buttonBg: '#ff69b4', // pink button
                buttonColor: '#101022', // black text on pink
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              },
              'gradient': {
                backgroundColor: 'linear-gradient(135deg, #ff69b4, #ff1493)', // pink gradient
                noticeColor: '#ffffff', // white text on pink gradient
                buttonBg: '#ffffff', // white button
                buttonColor: '#101022', // black text on white
                border: 'none',
                borderRadius: '16px'
              }
            },
            'membership-quiz': {
              'default': {
                backgroundColor: '#0000ff', // blue
                noticeColor: '#ffffff', // white text on blue
                buttonBg: '#ffffff', // white button
                buttonColor: '#0000ff', // blue text on white
                border: 'none',
                borderRadius: '8px'
              },
              'elegant': {
                backgroundColor: '#000080', // dark blue
                noticeColor: '#ffffff', // white text on dark blue
                buttonBg: '#ffffff', // white button
                buttonColor: '#000080', // dark blue text on white
                border: '2px solid #4169e1',
                borderRadius: '12px'
              },
              'soft': {
                backgroundColor: '#e6f3ff', // light blue
                noticeColor: '#0000ff', // blue text on light blue
                buttonBg: '#0000ff', // blue button
                buttonColor: '#ffffff', // white text on blue
                border: '1px solid #b3d9ff',
                borderRadius: '8px'
              },
              'corporate': {
                backgroundColor: '#ffffff', // white
                noticeColor: '#101022', // black text on white
                buttonBg: '#0000ff', // blue button
                buttonColor: '#ffffff', // white text on blue
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }
            },
            'media-buzz': {
              'default': {
                backgroundColor: '#ffffff', // white
                noticeColor: '#101022', // black text on white
                buttonBg: '#ffff00', // yellow button
                buttonColor: '#101022', // black text on yellow
                border: '2px dashed #101022',
                borderRadius: '8px'
              },
              'news': {
                backgroundColor: '#f8f9fa', // light gray (close to white)
                noticeColor: '#101022', // black text
                buttonBg: '#0000ff', // blue button
                buttonColor: '#ffffff', // white text on blue
                border: '1px solid #e0e0e0',
                borderRadius: '8px'
              },
              'viral': {
                backgroundColor: '#ffff00', // yellow
                noticeColor: '#101022', // black text on yellow
                buttonBg: '#101022', // black button
                buttonColor: '#ffffff', // white text on black
                border: '2px solid #ffd700',
                borderRadius: '12px'
              },
              'trending': {
                backgroundColor: '#e6f3ff', // light blue
                noticeColor: '#0000ff', // blue text on light blue
                buttonBg: '#0000ff', // blue button
                buttonColor: '#ffffff', // white text on blue
                border: '1px solid #b3d9ff',
                borderRadius: '10px'
              }
            }
          };

          return styleVariants[variant]?.[styleVariant] || styleVariants[variant]?.['default'] || {
            backgroundColor: '#ff69b4',
            noticeColor: '#101022',
            buttonBg: '#101022',
            buttonColor: '#ffffff',
            border: 'none',
            borderRadius: '8px'
          };
        };

        const variantStylesHTML = getVariantStylesHTML(element.variant, element.styleVariant);
        const boxShadow = element.styleVariant === 'gradient' ? '0 8px 32px rgba(255, 107, 107, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)';

        const buttonWithNoticeHTML = `
          <div style="background: ${variantStylesHTML.backgroundColor.includes('gradient') ? variantStylesHTML.backgroundColor : variantStylesHTML.backgroundColor}; padding: 20px; border-radius: ${variantStylesHTML.borderRadius}; text-align: center; margin-bottom: 4px; border: ${variantStylesHTML.border}; box-shadow: ${boxShadow};">
            <p style="color: ${variantStylesHTML.noticeColor}; font-weight: bold; margin-bottom: 16px; line-height: 1.5;">
              ${element.content?.notice?.replace(/\n/g, '<br>') || ''}
            </p>
            <p style="color: ${variantStylesHTML.noticeColor}; margin-bottom: 16px;">
              ${element.content?.callToAction || ''}
            </p>
            <a href="${element.content?.buttonUrl || '#'}" style="background-color: ${variantStylesHTML.buttonBg}; color: ${variantStylesHTML.buttonColor}; padding: 16px 32px; font-size: 18px; font-weight: bold; border: none; border-radius: 8px; text-decoration: none; display: inline-block; max-width: 400px; width: 100%; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.2s ease;">
              ${element.content?.buttonText || ''}
            </a>
          </div>
        `;
        return buttonWithNoticeHTML;
      case ELEMENT_TYPES.BUTTON_HEADLINE_PRODUCT_IMAGE:
        const buttonHeadlineProductImageHTML = `
          <div style="background: ${element.content?.background || '#ffffff'}; color: ${element.content?.textColor || '#333333'}; border-radius: 16px; margin-bottom: 4px; text-align: center; padding: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.07);">
            <h2 style="color: ${element.content?.textColor || '#333333'}; font-weight: bold; margin-bottom: 16px; font-size: 1.5rem;">
              ${element.content?.headline || 'Amazing Product Offer'}
            </h2>
            <div style="margin-bottom: 16px;">
              <img src="${element.content?.productImage || '/api/placeholder/400/300'}" alt="Product" style="width: 100%; max-width: 400px; height: auto; border-radius: 8px; border: 2px dashed #e0e0e0;" />
            </div>
            ${element.content?.subheadline ? `
              <p style="color: ${element.content?.textColor || '#333333'}; margin-bottom: 16px; line-height: 1.5;">
                ${element.content.subheadline.replace(/\n/g, '<br>')}
              </p>
            ` : ''}
            <a href="${element.content?.buttonUrl || '#'}" style="background-color: ${element.content?.buttonColor || '#4caf50'}; color: #fff; padding: 18px 0; font-size: 1.3rem; font-weight: bold; border: none; border-radius: 8px; text-decoration: none; display: block; width: 100%; margin: 16px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.2s ease;">
              ${element.content?.buttonText || 'Get This Offer Now!'}
            </a>
          </div>
        `;
        return buttonHeadlineProductImageHTML;
      case ELEMENT_TYPES.CUSTOM_LIST:
        const customListHTML = `
          <div style="background-color: ${element.styles?.backgroundColor || '#181818'}; color: ${element.styles?.color || '#fff'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1.25rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '24px'}; margin: ${element.styles?.margin || '0 0 16px 0'};">
            ${element.rows.map((row, idx) => `
              <div key="${idx}" style="display: flex; align-items: center; margin-bottom: 8px;">
                ${getListIconHTML(row.icon, idx)}
                <span style="margin-left: 8px; font-family: ${getPreset(element.textStylePreset || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${getPreset(element.textStylePreset || 'paragraph1')?.fontSize || element.styles?.fontSize || '1.25rem'}; color: ${getPreset(element.textStylePreset || 'paragraph1')?.color || element.styles?.color || '#fff'}; font-weight: ${getPreset(element.textStylePreset || 'paragraph1')?.fontWeight || 'inherit'}; line-height: ${getPreset(element.textStylePreset || 'paragraph1')?.lineHeight || 'inherit'}; letter-spacing: ${getPreset(element.textStylePreset || 'paragraph1')?.letterSpacing || 'inherit'};">${row.text}</span>
              </div>
            `).join('')}
          </div>
        `;
        return customListHTML;
      case ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE:
        const customListTwoLineHTML = `
          <div style="background-color: ${element.styles?.backgroundColor || '#181818'}; color: ${element.styles?.color || '#fff'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1.25rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '24px'}; margin: ${element.styles?.margin || '0 0 16px 0'};">
            ${element.rows.map((row, idx) => `
              <div key="${idx}" style="display: flex; align-items: flex-start; margin-bottom: 16px;">
                ${getListIconHTML(row.icon, idx)}
                <div style="margin-left: 8px; flex: 1;">
                  <div style="font-family: ${getPreset(element.textStylePreset1 || 'subheading1')?.fontFamily || element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${getPreset(element.textStylePreset1 || 'subheading1')?.fontSize || element.styles?.fontSize || '1.25rem'}; color: ${element.styles?.color || getPreset(element.textStylePreset1 || 'subheading1')?.color || '#fff'}; font-weight: ${getPreset(element.textStylePreset1 || 'subheading1')?.fontWeight || 'inherit'}; line-height: ${getPreset(element.textStylePreset1 || 'subheading1')?.lineHeight || 'inherit'}; letter-spacing: ${getPreset(element.textStylePreset1 || 'subheading1')?.letterSpacing || 'inherit'}; margin-bottom: 4px;">${row.text1}</div>
                  <div style="font-family: ${getPreset(element.textStylePreset2 || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${getPreset(element.textStylePreset2 || 'paragraph1')?.fontSize || element.styles?.fontSize || '1.25rem'}; color: ${element.styles?.color || getPreset(element.textStylePreset2 || 'paragraph1')?.color || '#fff'}; font-weight: ${getPreset(element.textStylePreset2 || 'paragraph1')?.fontWeight || 'inherit'}; line-height: ${getPreset(element.textStylePreset2 || 'paragraph1')?.lineHeight || 'inherit'}; letter-spacing: ${getPreset(element.textStylePreset2 || 'paragraph1')?.letterSpacing || 'inherit'};">${row.text2}</div>
                </div>
              </div>
            `).join('')}
          </div>
        `;
        return customListTwoLineHTML;
      case ELEMENT_TYPES.TIMELINE_LIST:
        const timelineListHTML = `
          <div style="background-color: ${element.styles?.backgroundColor || '#fff'}; color: ${element.styles?.color || '#222'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1.1rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '24px'}; margin: ${element.styles?.margin || '0 0 16px 0'};">
            ${element.steps.map((step, idx) => `
              <div key="${idx}" style="display: flex; align-items: flex-start; margin-bottom: 16px;">
                <div style="margin-right: 16px; display: flex; align-items: center; flex-direction: column;">
                  <div style="width: 24px; height: 24px; border-radius: 50%; background-color: ${step.dotColor}; margin-bottom: 8px;"></div>
                  ${idx < element.steps.length - 1 ? '<div style="width: 2px; height: 100%; background-color: #e0e0e0;"></div>' : ''}
                </div>
                <div>
                  <p style="color: ${step.stageLabelColor}; font-weight: bold; margin-bottom: 8px;">${step.stageLabel}</p>
                  <h3 style="font-weight: bold; margin-bottom: 8px;">${step.title}</h3>
                  <p>${step.description}</p>
                </div>
              </div>
            `).join('')}
          </div>
        `;
        return timelineListHTML;
      case ELEMENT_TYPES.PROCESS_STEPS:
        const processStepsHTML = `
          <div style="background-color: ${element.styles?.backgroundColor || '#fff'}; color: ${element.styles?.color || '#333'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '24px'}; margin: ${element.styles?.margin || '0 0 16px 0'};">
            ${element.steps.map((step, idx) => `
              <div key="${idx}" style="display: flex; align-items: flex-start; gap: 24px; margin-bottom: ${idx < element.steps.length - 1 ? '32px' : '0'};">
                <div style="flex-shrink: 0; width: 150px; position: relative;">
                  <img src="${step.image}" alt="Step ${step.number}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 12px; border: 1px solid #e0e0e0;" onerror="this.src='https://via.placeholder.com/150x120?text=Step+${step.number}'" />
                  <div style="position: absolute; top: -8px; right: -8px; width: 36px; height: 36px; border-radius: 50%; background-color: ${step.numberColor || '#1976d2'}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; font-weight: bold; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
                    ${step.number}
                  </div>
                </div>
                <div style="flex: 1; padding-top: 8px;">
                  <h3 style="font-weight: bold; margin: 0 0 8px 0; color: inherit;">${step.title}</h3>
                  <p style="margin: 0; color: inherit; line-height: 1.5;">${step.description}</p>
                </div>
              </div>
            `).join('')}
          </div>
        `;
        return processStepsHTML;
      case ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE:
        const textWithRightImageHTML = `
          <div style="background-color: ${element.styles?.backgroundColor || '#fff'}; color: ${element.styles?.color || '#333'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1.1rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '20px'}; margin: ${element.styles?.margin || '0 0 16px 0'}; display: flex; align-items: center; gap: 24px;">
            <div style="flex: 1;">
              <h3 style="margin: 0; font-weight: bold; color: inherit;">${element.content.text}</h3>
            </div>
            <div style="flex-shrink: 0; width: 180px;">
              <img src="${element.content.image}" alt="Right side image" style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px; border: 1px solid #e0e0e0;" onerror="this.src='https://via.placeholder.com/180x120?text=Image'" />
            </div>
          </div>
        `;
        return textWithRightImageHTML;
      case ELEMENT_TYPES.LEFT_IMAGE_TEXT:
        const alignItems = element.imageAlignment === 'top' ? 'flex-start' :
          element.imageAlignment === 'bottom' ? 'flex-end' : 'center';
        const leftImageTextHTML = `
          <div style="display: flex; gap: ${element.spacing || 20}px; margin-bottom: 4px; ${Object.entries(element.styles || {})
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ')}; align-items: ${alignItems};">
            <div style="flex-shrink: 0;">
              <img src="${element.content.image}" alt="Profile image" style="width: ${element.imageSize || 120}px; height: ${element.imageSize || 120}px; object-fit: cover; border-radius: 50%; border: 3px solid #e0e0e0;" onerror="this.src='https://via.placeholder.com/150x150?text=Profile'" />
            </div>
            <div style="flex: 1;">
              <p style="margin: 0; color: ${element.textStyles?.color || 'inherit'}; line-height: ${element.textStyles?.lineHeight || '1.6'}; font-size: ${element.textStyles?.fontSize || '16px'}; font-weight: ${element.textStyles?.fontWeight || 'normal'}; font-family: ${element.textStyles?.fontFamily || 'inherit'}; text-align: ${element.textStyles?.textAlign || 'left'}; background-color: ${element.textStyles?.backgroundColor || 'transparent'}; padding: ${element.textStyles?.padding || '0px'}; border-radius: ${element.textStyles?.borderRadius || '0px'};">${element.content.text}</p>
            </div>
          </div>
        `;
        return leftImageTextHTML;
      case ELEMENT_TYPES.QUOTATION_BLOCK:
        return `
            <div style="
              position: relative;
              background: ${element.styles?.backgroundColor || '#fce7f3'};
              color: ${element.styles?.color || '#101022'};
              border-radius: ${element.styles?.borderRadius || '16px'};
              padding: ${element.styles?.padding || '24px 32px 24px 32px'};
              font-style: italic;
              min-height: 120px;
              margin-bottom: 16px;
              overflow: visible;
              font-size: ${element.styles?.fontSize || '18px'};
              font-weight: ${element.styles?.fontWeight || '400'};
              line-height: ${element.styles?.lineHeight || '1.5'};
              font-family: ${element.styles?.fontFamily || "'Inter', sans-serif"};
            ">
              <div style="
                position: absolute;
                top: -12px;
                left: 12px;
                width: 40px;
                height: 40px;
                background: #fff;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                z-index: 2;
              ">
                <span style="font-size: 24px; font-family: serif; font-weight: bold; color: #101022;">&ldquo;</span>
              </div>
              <span style="
                font-style: italic;
                font-size: ${element.styles?.fontSize || '18px'};
                font-weight: ${element.styles?.fontWeight || '400'};
                line-height: ${element.styles?.lineHeight || '1.5'};
                font-family: ${element.styles?.fontFamily || "'Inter', sans-serif"};
                display: block;
              ">
                ${element.content || ''}
              </span>
            </div>
          `;
      case ELEMENT_TYPES.IMAGE_OVERLAY: {
        const finalImgWidth = typeof element.styles?.width === 'number' ? `${element.styles.width}px` : (element.styles?.width || '100%');
        const finalImgHeight = typeof element.styles?.height === 'number' ? `${element.styles.height}px` : (element.styles?.height || 'auto');

        const imgOverlayStyles = Object.entries(element.styles || {})
          .map(([key, value]) => {
            if ((key === 'width' || key === 'height') && typeof value === 'number') return `${key}: ${value}px`;
            return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`;
          })
          .join('; ');

        const overlayStylesStr = Object.entries(element.overlayStyles || {})
          .map(([key, value]) => {
            return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`;
          })
          .join('; ');

        const overlayZone = element.zones[0];
        const overlayZoneElementsHTML = (overlayZone?.elements || []).map(ze => generateElementHTML(ze)).join('\n');

        const imageSource = element.imageSrc || element.content?.image || '';
        const position = element.imagePosition || { x: 0, y: 0 };
        const scale = element.imageScale || 1;

        return `
          <div class="image-overlay-wrapper" style="position: relative; line-height: 0; box-sizing: border-box; width: ${finalImgWidth}; height: ${finalImgHeight}; margin: 0 auto; overflow: hidden; ${imgOverlayStyles}">
            <img src="${imageSource}" style="position: absolute; left: 50%; top: 0; transform: translate(-50%, ${position.y}px) scale(${scale}); transform-origin: top center; max-width: none; max-height: none; display: block;" alt="Background">
            <div class="overlay-content-box" style="box-sizing: border-box; position: absolute; z-index: ${element.overlayStyles?.zIndex || 10}; ${overlayStylesStr}">
              ${overlayZoneElementsHTML}
            </div>
          </div>
        `;
      }

      case ELEMENT_TYPES.LAYOUT: {
        const config = LAYOUT_CONFIGS[element.layoutType];
        if (!config) {
          console.warn('Invalid layout type for HTML generation:', element.layoutType);
          return '';
        }

        // Special handling for INLINE_ROW layout
        if (element.layoutType === LAYOUT_TYPES.INLINE_ROW) {
          const layoutStyle = Object.entries(element.styles || {})
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');

          if (!element.zones || !Array.isArray(element.zones)) {
            console.warn('Inline row layout missing zones for HTML generation:', element);
            return `<div class="inline-row-container" style="${layoutStyle}">Inline row with no zones</div>`;
          }

          const zone = element.zones[0]; // Inline row has only one zone
          if (!zone) {
            console.warn('Invalid zone found for inline row HTML generation:', element);
            return `<div class="inline-row-container" style="${layoutStyle}">Invalid zone</div>`;
          }

          const zoneElements = (zone.elements || []).map(zoneElement => {
            if (!zoneElement) {
              console.warn('Invalid zone element found for inline row HTML generation:', zoneElement);
              return '';
            }
            return generateElementHTML(zoneElement);
          }).join('\n');

          return `<div class="inline-row-container" style="display: flex; align-items: center; gap: 10px; margin-bottom: 4px; ${layoutStyle}">${zoneElements}</div>`;
        }

        const layoutStyle = Object.entries(element.styles || {})
          .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
          .join('; ');

        // Add safety check for zones
        if (!element.zones || !Array.isArray(element.zones)) {
          console.warn('Layout element missing zones for HTML generation:', element);
          return `<div class="layout-container" style="${layoutStyle}">Layout with no zones</div>`;
        }

        // Determine if this is a multi-column layout (needed for zone styling)
        const isMultiColumn = element.layoutType === LAYOUT_TYPES.TWO_COLUMN ||
          element.layoutType === LAYOUT_TYPES.THREE_COLUMN ||
          element.layoutType === LAYOUT_TYPES.FEATURE ||
          element.layoutType === LAYOUT_TYPES.SINGLE_ROW_2 ||
          element.layoutType === LAYOUT_TYPES.SINGLE_ROW_3 ||
          element.layoutType === LAYOUT_TYPES.SINGLE_ROW_4 ||
          element.layoutType === LAYOUT_TYPES.SINGLE_ROW_5;

        const zonesHTML = element.zones.map((zone, index) => {
          if (!zone) {
            console.warn('Invalid zone found at index for HTML generation:', index);
            return '<div class="zone" style="flex: 1; padding: 10px; min-width: 280px;">Invalid zone</div>';
          }

          const zoneElements = (zone.elements || []).map(zoneElement => {
            if (!zoneElement) {
              console.warn('Invalid zone element found for HTML generation:', zoneElement);
              return '';
            }
            return generateElementHTML(zoneElement);
          }).join('\n');

          // Get zone width and height from config to match canvas/preview rendering
          const zoneConfig = config.zones[index];
          const zoneWidth = zoneConfig ? zoneConfig.width : '50%';

          // Get zone height style to match canvas/preview rendering
          const zoneHeightStyle = getZoneHeightStyle(zone.id, zoneConfig?.height || 'auto');
          const zoneHeightCSS = Object.entries(zoneHeightStyle)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');

          const zoneStyle = isMultiColumn
            ? `flex: 1; min-width: 0; padding: 6px; text-align: ${zoneConfig?.textAlign || zone.textAlign || 'left'}; border: 1px dashed #e0e0e0; border-radius: 4px; word-wrap: break-word; overflow-wrap: break-word; box-sizing: border-box; ${zoneHeightCSS};`
            : `width: 100%; padding: 6px; text-align: ${zoneConfig?.textAlign || zone.textAlign || 'left'}; border: 1px dashed #e0e0e0; border-radius: 4px; word-wrap: break-word; overflow-wrap: break-word; box-sizing: border-box; ${zoneHeightCSS};`;

          return `<div class="zone zone-${element.id}" style="${zoneStyle}">${zoneElements}</div>`;
        }).join('\n');

        // Apply responsive flexDirection and flexWrap based on layout type to match canvas behavior
        let flexDirection = 'row';
        let flexWrap = 'nowrap';
        if (element.layoutType === LAYOUT_TYPES.THREE_COLUMN) {
          flexDirection = 'wrap';
          flexWrap = 'wrap';
        }

        // Add responsive CSS to match preview mode behavior
        const responsiveCSS = `
          /* Mobile portrait mode - side-by-side for multi-column layouts */
          .layout-container-${element.id} {
            flex-direction: ${isMultiColumn ? 'row' : 'column'} !important;
            flex-wrap: nowrap !important;
            gap: 8px !important;
          }
          .zone-${element.id} {
            ${isMultiColumn ? 'flex: 1 !important; min-width: 0 !important;' : 'width: 100% !important;'}
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
        `;

        // Get layout border to match canvas styling
        const layoutBorder = element.styles?.border || '1px dashed #ccc';
        const layoutBorderRadius = element.styles?.borderRadius || '4px';
        const layoutPadding = element.styles?.padding || '8px';
        const layoutBackgroundColor = element.styles?.backgroundColor || 'transparent';

        return `<style>${responsiveCSS}</style><div class="layout-container layout-container-${element.id}" style="display: flex; gap: 6px; margin-bottom: 4px; flex-direction: ${isMultiColumn ? 'row' : 'column'}; flex-wrap: nowrap; border: ${layoutBorder}; border-radius: ${layoutBorderRadius}; padding: ${layoutPadding}; background-color: ${layoutBackgroundColor}; ${layoutStyle}">${zonesHTML}</div>`;
      }
      case ELEMENT_TYPES.FAQ_ACCORDION:
        const faqId = `faq-${element.id || Math.random().toString(36).substr(2, 9)}`;
        const faqHTML = `
          <div style="${styleString}; background-color: ${element.containerBackgroundColor || 'transparent'}; border-radius: ${element.itemBorderRadius || '4px'}; padding: ${element.containerBackgroundColor !== 'transparent' ? '16px' : '0'};">
            ${element.title ? `<h3 style="text-align: ${element.textAlign || 'center'}; margin-bottom: 24px; font-weight: bold; color: ${element.titleTextColor === 'primary' ? '#1976d2' : element.titleTextColor || '#1976d2'};">${element.title}</h3>` : ''}
            <div class="faq-container" id="${faqId}">
              ${element.items.map((item, index) => `
                <div class="faq-item" style="margin-bottom: ${element.itemSpacing || '12px'}; ${element.showDivider !== false && index < element.items.length - 1 ? `border-bottom: 2px solid ${element.dividerColor || '#C1A7F1'}; padding-bottom: 16px;` : ''}">
                  <div class="faq-question" style="display: flex; justify-content: space-between; align-items: center; padding: ${element.questionPadding || '16px'}; cursor: pointer; background-color: ${element.questionBackgroundColor || 'transparent'}; border-radius: ${element.itemBorderRadius || '4px'}; margin-bottom: 2px; transition: background-color 0.2s ease; text-align: ${element.textAlign || 'left'};" 
                       onclick="toggleFAQItem('${faqId}', ${index}, '${element.iconType || 'plusMinus'}')">
                    <span style="font-weight: bold; color: ${element.questionTextColor || '#4B329A'}; flex: 1; padding-right: ${element.showIcon !== false ? '16px' : '0'}; line-height: 1.4; text-align: ${element.textAlign || 'left'};">${item.question}</span>
                    ${element.showIcon !== false ? `
                      <span class="faq-icon" id="${faqId}-icon-${index}" style="color: ${element.iconColor || '#36C3D6'}; font-size: ${element.iconSize === 'small' ? '18px' : element.iconSize === 'medium' ? '24px' : '32px'}; font-weight: bold; flex-shrink: 0;">
                        ${element.iconType === 'expandMore' ? '▼' : element.iconType === 'arrow' ? '↓' : element.iconType === 'arrowDrop' ? '▼' : '+'}
                      </span>
                    ` : ''}
                  </div>
                  <div class="faq-answer" id="${faqId}-answer-${index}" style="display: none; padding: ${element.answerPadding || '16px'}; background-color: ${element.answerBackgroundColor || 'rgba(0, 0, 0, 0.02)'}; border-radius: ${element.itemBorderRadius || '4px'}; margin-top: 8px; line-height: 1.6; color: ${element.answerTextColor || '#4B329A'}; text-align: ${element.textAlign || 'left'};">
                    ${item.answer}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `;
        return faqHTML;
      default:
        return '';
    }
  };
